import type { QueueStageValue } from './api.ts'
import { STATUS_TONE } from './vocabulary.ts'

/**
 * Whether a queue row offers its own refresh, and how it says so.
 *
 * Two statuses wait on somebody outside this screen. "Aguardando correção"
 * waits on comercial to change the ERP; "Sem receita" waits on the pharmacy to
 * attach the receita (#300). Either way the thing to do is look again, and the
 * icon is the affordance.
 *
 * A sem-receita one offers it only while the requisition is still on the
 * bench: in production the preparation has been made, and a prescription
 * found now changes nothing. `stage` is the row's, read on the account's
 * positions (`stageOf`); undefined until the session has said where its stages
 * start, and an unknown stage offers nothing -- the refresh is a paid re-read,
 * and a row in production never offered one.
 *
 * Coloured like the badge it sits beside, from the same table, so the two
 * cannot drift. It used to be amber for both, because it was born beside the
 * amber "Aguardando correção" badge -- and beside a grey "Sem receita" an
 * amber icon reads as a warning nobody raised.
 */
export interface RowRefresh {
  readonly tooltip: string
  readonly label: string
  readonly color: string
}

export function rowRefresh(
  status: string,
  stage: QueueStageValue | undefined,
): RowRefresh | undefined {
  if (status === 'awaiting_correction') {
    return {
      tooltip: 'Já corrigido no sistema? Clique para reconferir.',
      label: 'Reconferir: já corrigido no sistema',
      color: STATUS_TONE['awaiting_correction']!.text,
    }
  }
  if (status === 'discarded' && stage !== undefined && stage !== 'producao') {
    return {
      tooltip: 'A receita foi anexada depois? Clique para procurá-la de novo.',
      label: 'Reconferir: procurar a receita de novo',
      color: STATUS_TONE['discarded']!.text,
    }
  }
  return undefined
}
